import { Text } from '@/components/commons/typography/Text'
import { Title } from '@/components/commons/typography/Title'
import { TextSkeleton } from '@/components/skeleton/TextSkeleton'
import { Icon, PlusIcon } from '@/public/assets/v2/images/icons'
import {
  cssClass,
  formatNumberWithSuffix,
  getHostName,
  getLinkSafeUrl,
} from '@/shared/utils'
import { vars } from '@/styles/v2/vars.css'
import { Collapse } from '@mantine/core'
import { statMaxWidth } from 'containers/highlights/styles/highlight.css'
import { Highlight } from 'containers/highlights/types/highlights'
import { useProfile } from 'containers/profile/hooks/useProfile'
import { User } from 'containers/user/types/user'
import Link from 'next/link'
import { useEffect, useState } from 'react'

interface HighlightCardContentProps {
  highlight?: Highlight
  loading?: boolean
  defaultOpen?: boolean
  inModal?: boolean
  overrideProfile?: User
}

export const HighlightCardContent = ({
  highlight,
  loading,
  defaultOpen,
  inModal,
  overrideProfile,
}: HighlightCardContentProps) => {
  const { profile: currentProfile } = useProfile()
  const profile = overrideProfile || currentProfile
  const [showDetails, setShowDetails] = useState(false)
  const safeUrl = getLinkSafeUrl(highlight?.contentLink)

  useEffect(() => {
    if (defaultOpen) setShowDetails(true)
  }, [defaultOpen])

  return (
    <div className="bg-white-primary rounded-b-12">
      {highlight && (
        <Collapse in={showDetails || inModal}>
          {/* <div className="flex gap-10 items-center full-w p-12 border-b-solid border-secondary border-b-1">
            <div>
              <SocialIconLink
                social={ISocialPlatform.INSTAGRAM}
                size={24}
                onClick={() => {}}
                username="test"
              />
            </div>
            <Text
              as="span"
              variation="xSmall"
              colorClass="text-secondary"
              weightClass="font-bold"
            >
              @{highlight.contentType}
            </Text>
          </div> */}
          <div className="p-12 flex flex-column gap-16">
            <div>
              {inModal && (
                <Title
                  as="h3"
                  sizeClass="text-16"
                  className="break-text"
                  weightClass="font-bold"
                >
                  {highlight.title}
                </Title>
              )}

              {!inModal && highlight?.contentLink && (
                <Link
                  href={safeUrl}
                  className={cssClass([
                    'text-14 font-bold flex items-center',
                    highlight?.stats?.length > 0 && 'mb-16',
                  ])}
                  target="_blank"
                  style={{ color: vars.colors.pink.primary }}
                >
                  <Icon.NewTabIcon className="cursor-pointer text-18" />
                  &nbsp;
                  {getHostName(safeUrl).toLocaleLowerCase()}
                </Link>
              )}

              <div
                className={cssClass(['flex wrap gap-12', inModal && 'pt-8'])}
              >
                {highlight?.stats
                  ?.filter((stat) => stat.visible)
                  ?.map((stat) => (
                    <div
                      key={profile.id + '_stat_highlight_' + stat.name}
                      className={cssClass([
                        'bg-primary py-6 px-16 rounded-12 flex-1_3',
                        statMaxWidth,
                      ])}
                    >
                      <Text sizeClass="text-13" colorClass="text-tertirary">
                        {stat.name}
                      </Text>
                      <Text
                        sizeClass="text-13"
                        colorClass="text-scondary"
                        weightClass="font-bold"
                      >
                        {formatNumberWithSuffix(stat.value)}
                      </Text>
                    </div>
                  ))}
              </div>
            </div>

            {highlight?.about && (
              <div>
                <Title as="h3" sizeClass="text-14" weightClass="font-bold">
                  About this Highlight
                </Title>
                <Text
                  sizeClass="text-14"
                  colorClass="text-tertirary"
                  style={{ whiteSpace: 'pre-line' }}
                >
                  {highlight.about}
                </Text>
              </div>
            )}

            {highlight?.contentCategories?.length > 0 && (
              <div>
                <Title
                  as="h3"
                  sizeClass="text-14"
                  className="break-text"
                  weightClass="font-bold"
                >
                  Content Categories
                </Title>
                <div className="flex items-center wrap gap-6 mt-10">
                  {highlight?.contentCategories?.map((category) => (
                    <div
                      key={`${category}_${highlight.id}`}
                      className="px-10 py-10 bg-tertiary rounded-8"
                    >
                      <Text
                        sizeClass="text-13"
                        colorClass="text-primary"
                        className="break-text"
                        lineHeightClass="leading-14"
                      >
                        {category}
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Collapse>
      )}

      {!inModal && (
        <div
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center justify-center cursor-pointer gap-6 border-t-solid border-secondary border-t-1  p-12"
        >
          {loading ? (
            <TextSkeleton width={100} height={16} />
          ) : (
            <>
              <Text variation="xSmall">
                {!showDetails ? 'Expand for full details' : 'Collapse'}
              </Text>
              {!showDetails ? (
                <PlusIcon className="text-12 text-primary" />
              ) : (
                <Text as="span" sizeClass="text-16" weightClass="font-bold">
                  -
                </Text>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
